'use client';

import { RingSizes, RingChargerType } from 'content/products/ring';
// UHX Lifetime for RARE is handled by RareUhxLifetimeShopifyMap, not regular UhxShopifyMap
import {
  AddonsAction,
  AddonsState,
  CheckoutParams,
  RingContextType,
  ShippingEstimateObject,
} from './types';
export { fetchEstimatesForStore } from './estimates';
import {
  RareColors,
  productMap_rare,
  RareSizingKitShopifyMap,
  RarePowerPlugsBundleShopifyMap,
  RareUhxLifetimeShopifyMap,
} from 'content/products/rare';

import { ReactNode, useRef, useEffect } from 'react';
import styles from './checkout.module.css';

import { DaysByDate } from 'components/shared/DaysbyDate';
import { addToCartArgsType, ProductType } from 'components/shared/Cart/types';
import { tagBundle } from 'components/shared/Cart/bundle';
import { Currency } from 'utils/Currency';

export function getCheckoutLineItems(
  params: CheckoutParams,
): addToCartArgsType[] {
  const {
    color,
    size,
    store,
    eSizingSize,
    // uhxCart no longer needed - using RareUhxLifetimeShopifyMap directly
    misc,
  } = params;

  if (!color || !size || !store) {
    console.error('RARE getCheckoutLineItems: Missing required params', {
      color,
      size,
      store,
    });
    return [];
  }

  const productMap = productMap_rare;

  const productNode = productMap[color]?.[size];
  if (!productNode) {
    console.error('RARE getCheckoutLineItems: No product node found', {
      color,
      size,
      productMap: !!productMap,
    });
    return [];
  }

  let mainVariantId: string | undefined;
  if (misc && productNode.misc?.[misc]) {
    mainVariantId = productNode.misc[misc];
  } else {
    mainVariantId = productNode?.variantId[store];
  }
  if (!mainVariantId) {
    console.error('RARE getCheckoutLineItems: No mainVariantId found', {
      misc,
      productNode: productNode?.variantId,
      store,
    });
    return [];
  }

  // Build noteAttributes for eSizing only (no engraving for rare)
  const noteAttributes: Array<{ key: string; value: string }> = [];
  if (eSizingSize) noteAttributes.push({ key: 'eSizing', value: 'true' });

  const lineItems: addToCartArgsType[] = [];

  // Add a Sizing Kit if "open" size is chosen
  if (
    size === RingSizes.OPEN &&
    RareSizingKitShopifyMap[store]?.shopifyVariantId
  ) {
    lineItems.push({
      shopifyVariantId: RareSizingKitShopifyMap[store]
        .shopifyVariantId as string,
      productType: ProductType.RING_RARE,
    });
  }

  // Note: Travel charger is not available for RARE products
  // No travel charger logic needed

  // RARE products automatically include these addons with every ring purchase:
  // - PowerPlugs Bundle (AFib + Cycle and Ovulation Pro + Tesla Powerplug + M1 Lite) - Single variant ID
  // - UHX one year - Separate variant ID

  // Automatically add PowerPlugs Bundle (included with every RARE purchase)
  // This includes: AFib, Cycle and Ovulation Pro, Tesla Powerplug, and M1 Lite collectively
  if (RarePowerPlugsBundleShopifyMap[store]?.shopifyVariantId) {
    lineItems.push({
      shopifyVariantId: RarePowerPlugsBundleShopifyMap[store]
        .shopifyVariantId as string,
      productType: ProductType.RING_RARE,
    });
  }

  // Automatically add UHX Lifetime addon (included with every RARE purchase)
  // RARE uses UHX Lifetime, not Year1/Year2 subscriptions
  if (RareUhxLifetimeShopifyMap[store]?.shopifyVariantId) {
    lineItems.push({
      shopifyVariantId: RareUhxLifetimeShopifyMap[store]
        .shopifyVariantId as string,
      productType: ProductType.GENERIC,
    });
  }

  // Note: Cardio Adaptibility is NOT available for RARE products

  // The primary ring line item must be pushed last so the CartProvider can
  // pick it as the primary variant for upsell logic.
  lineItems.push({
    shopifyVariantId: mainVariantId,
    productType: ProductType.RING_RARE,
    params:
      noteAttributes.length > 0
        ? { productType: 'ring', noteAttributes }
        : undefined,
  });

  return tagBundle(lineItems);
}

export const ShippingLabel = ({
  mcf = false,
  content,
  justContent = false,
  isMinEstimate = false,
}: {
  mcf?: boolean;
  content: ReactNode;
  justContent?: boolean;
  isMinEstimate?: boolean;
}) => {
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
  }, [content, mcf]);

  if (justContent) {
    return (
      <span key={renderCount.current} className={styles.root}>
        {content}
      </span>
    );
  }

  return (
    <span className={styles.root} key={renderCount.current}>
        {/* <span>Dispatched by: {content}</span> */}
        <span>Each ring is made to order. Est. dispatch, {content}</span>
        {/* {mcf ? (
          <Image
            src={getCompressedAssetUrl('/web_v2/ring-buy/amazon-fba-2.png')}
            alt="Fulfillment by Amazon"
            width={'70'}
            height={'20'}
          />
        ) : null} */}
        {isMinEstimate ? <i className="min-est-dot"></i> : null}
      </span>
    );
};
export function getShippingEstimate({
  color,
  size,
  estimates,
  addonsState, // eslint-disable-line @typescript-eslint/no-unused-vars
  t,
}: {
  color: RareColors | null;
  size: RingSizes | null;
  estimates: ShippingEstimateObject | undefined;
  addonsState: AddonsState;
  t: (key: string) => string;
}) {
  if (!estimates || !size || !color) {
    return (
      <ShippingLabel
        content="Dispatch time based on ring selection."
        justContent={true}
      />
    );
  }

  const productMap = productMap_rare;

  const selectedSKU = (productMap as any)[color]?.[size]?.SKU ?? null;

  if (!selectedSKU) return null;

  const selectedSKUEstimateObject = estimates[selectedSKU];

  // Rare products don't have engraving, so MCF is always valid
  const mcfValidOnSelection = true;

  if (!selectedSKUEstimateObject) {
    return (
      <ShippingLabel
        content="Shipping estimate not available."
        justContent={true}
      />
    );
  }

  const specificEstimate = (() => {
    if (mcfValidOnSelection && selectedSKUEstimateObject.mcf_for_eta)
      return selectedSKUEstimateObject.date;
    else return selectedSKUEstimateObject.default_custom_eta;
  })();

  const monthFormatter = new Intl.DateTimeFormat('default', {
    month: 'short',
  });

  if (specificEstimate) {
    const minDate = new Date(specificEstimate);
    const minMonth = minDate.getMonth();
    const minDay = minDate.getDate();

    if (size === RingSizes.OPEN) {
      return (
        <ShippingLabel
          mcf={
            mcfValidOnSelection &&
            (selectedSKUEstimateObject?.show_mcf_logo ?? false)
          }
          content={
            <>
              <span style={{ whiteSpace: 'nowrap' }}>
                <DaysByDate endDate={minDate} />
              </span>{' '}
              <span>({t('ring.AFTER_SIZE_SELECTION')})</span>
            </>
          }
        />
      );
    }

    const maxDate = new Date(specificEstimate);
    maxDate.setDate(maxDate.getDate() + 2);
    const maxMonth = maxDate.getMonth();
    const maxDay = maxDate.getDate();

    return (
      <ShippingLabel
        mcf={
          mcfValidOnSelection &&
          (selectedSKUEstimateObject?.mcf_for_eta ?? false)
        }
        content={
          <span>
            {`${monthFormatter.format(minDate)} ${minDay} - ${
              minMonth === maxMonth ? '' : monthFormatter.format(maxDate)
            } ${maxDay}`}
          </span>
        }
      />
    );
  } else {
    return (
      <ShippingLabel
        content="Shipping estimate not available."
        justContent={true}
      />
    );
  }
}

export const initialRareSelectionContext: RingContextType = {
  size: RingSizes.OPEN,
  setSize: () => undefined,
  setColor: () => undefined,
  chargerType: RingChargerType.OG, // Travel charger not available for RARE
  setChargerType: () => undefined,
  defaultCurrency: Currency.inr,
  uhx: true, // UHX is automatically included with RARE
  setUhx: () => undefined,
  uhxCart: null, // UHX Lifetime handled separately via RareUhxLifetimeShopifyMap
  setUhxCart: () => undefined,
  afibCart: null, // AFib is now part of PowerPlugs bundle (no individual control)
  setAfibCart: () => undefined,
  cardioAdaptibilityCart: null, // Cardio Adaptibility is NOT available for RARE products
  setCardioAdaptabilityCart: () => undefined,
  cnoProCart: null, // CNO Pro is now part of PowerPlugs bundle (no individual control)
  setCnoProCart: () => undefined,
  checkout: async () => undefined,
  isLoading: false,
  price: 0,
  ultraHumanPrice: 0,
  effectivePrice: 0,
  chargerPrice: 0,
  shippingEstimate: null,
  affiliateDetails: null,
  store: null,
  campaignActive: false,
  affiliateDiscount: 0,
  goToNextStep: () => undefined,
  effectiveDiscount: { type: '', amount: 0 },
  sectionRefs: {},
  priceDrop: 0,
  isDiscountEnabled: false,
  showStrikedPrice: false,
  notServicable: false,
  notServicableMessage: undefined,
  isSizingKitSelected: true,
  setIsSizingKitSelected: () => undefined,
  handleSizeSelection: () => undefined,
  handleSizingKitClick: () => undefined,
  getUhxCoveragePrice: 0,
  getLifetimeSoftwarePrice: 0,
};

export function addonsReducer(
  state: AddonsState,
  action: AddonsAction,
): AddonsState {
  switch (action.type) {
    case 'SET_UHX_CART':
      return { ...state, uhxCart: action.payload };
    case 'SET_AFIB_CART':
      return { ...state, afibCart: action.payload };
    case 'SET_CARDIO_ADAPTIBILITY_CART':
      return { ...state, cardioAdaptibilityCart: action.payload };
    case 'SET_CNO_PRO_CART':
      return { ...state, cnoProCart: action.payload };

    default:
      return state;
  }
}

export const initialAddonsState: AddonsState = {
  uhxCart: null, // UHX Lifetime handled separately via RareUhxLifetimeShopifyMap
  afibCart: null, // AFib is now part of PowerPlugs bundle (no individual control)
  cardioAdaptibilityCart: null, // Cardio Adaptibility is NOT available for RARE products
  cnoProCart: null, // CNO Pro is now part of PowerPlugs bundle (no individual control)
};
